import {css, html, LitElement} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {ChangeType, ReportPermissionChange} from '../services';

@customElement('permission-changes')
export class PermissionChanges extends LitElement {
  static styles = css`
    .container {
      box-sizing: border-box;
      padding: 0.25rem;
      max-height: 500px;
      overflow: auto;
    }

    .entry {
      border-radius: 0.5rem;
      overflow: hidden;
      transition: box-shadow 0.1s ease-in-out;

      &.expanded {
        box-shadow: var(--card-box-shadow);
      }
    }

    .expandable {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.25rem;
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }

      &.expanded {
        background-color: rgba(0, 0, 0, 0.03);
      }

      .expand-icon {
        transition: transform 0.3s ease-in-out;

        &.expanded {
          transform: rotate(180deg);
        }
      }
    }

    .expandable-content {
      max-height: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      margin-left: 0.5rem;
      gap: 0.5rem;
      transition: max-height 0.2s ease-in-out, padding 0.2s ease-in-out;

      &.expanded {
        max-height: 200px;
        overflow: auto;
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
        animation: hide-scroll 0.2s backwards;
      }
    }

    .diff {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &.added {
        color: rgb(15, 62, 15);
      }

      &.removed {
        color: rgb(86, 21, 21);
      }

      &.updated {
        color: rgb(21, 70, 86);
      }
    }

    .count-badge {
      opacity: 0.5;
      font-size: 0.75rem;
    }

    @keyframes hide-scroll {
      from, to {
        overflow: hidden;
      }
    }
  `;

  @property({type: Object, attribute: "data"}) permissionChanges: ReportPermissionChange[] = [];

  @state() expandedItem: string | null = null;

  toggleExpand(reportKey: number, userKey: number) {
    const itemKey = this.getItemKey(reportKey, userKey);
    this.expandedItem = this.expandedItem === itemKey ? null : itemKey;
  }

  getItemKey(reportKey: number, userKey: number): string {
    return `${reportKey}-${userKey}`;
  }

  getIconName(changeType: ChangeType) {
    switch (changeType) {
      case ChangeType.Added:
        return 'add_circle';
      case ChangeType.Removed:
        return 'remove_circle';
      case ChangeType.Updated:
        return 'swap_vertical_circle';
      default:
        return 'help_outline';
    }
  }

  render() {
    return html`
      <div class="container">
        ${this.permissionChanges.map(pc => {
          const expanded = this.expandedItem === this.getItemKey(pc.reportKey, pc.userKey) ? 'expanded' : '';
          return html`
            <div class="entry ${expanded}">
              <div class="expandable ${expanded}"
                   @click="${() => this.toggleExpand(pc.reportKey, pc.userKey)}">
                <div class="diff ${pc.changeType.toLowerCase()}">
                  <mwc-icon>${this.getIconName(pc.changeType)}</mwc-icon>
                  <span>${pc.changeType} access to report <b>${pc.reportName}</b> for user <b>${pc.userName}</b></span>
                </div>
                <div style="display: flex; align-items: center">
                  <b class="count-badge">${pc.facilityChanges.length}</b>
                  <mwc-icon class="expand-icon ${expanded}" style="user-select: none">arrow_drop_down</mwc-icon>
                </div>
              </div>

              <div class="expandable-content ${expanded}">
                ${pc.facilityChanges.map(fac => html`
                  <div class="diff ${fac.changeType.toLowerCase()}">
                    <mwc-icon style="color: lightgray">subdirectory_arrow_right</mwc-icon>
                    <mwc-icon>${this.getIconName(fac.changeType)}</mwc-icon>
                    <span>
                      ${fac.changeType} ${fac.decrypt ? 'decrypt' : 'non-decrypt'} access
                      ${fac.facilityKey
                        ? html` to facility <b>${fac.facilityName}</b>`
                        : html` (report only)`
                      }
                    </span>
                  </div>
                `)}
              </div>
            </div>
          `;
        })}
      </div>
    `;
  }
}
